import React, { useState, useEffect, useCallback } from 'react';
import axios from 'axios';
import { API } from '../config/api';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Badge } from '../components/ui/badge';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../components/ui/tabs';
import { Card, CardContent } from '../components/ui/card';
import { Check, FileText, Archive, Loader2, Search, X } from 'lucide-react';
import { toast } from 'sonner';

import { AdminLogin } from '../components/admin/AdminLogin';
import { StatsCards } from '../components/admin/StatsCards';
import { QuotesList } from '../components/admin/QuotesList';
import { HandymenTab } from '../components/admin/HandymenTab';
import { GalleryTab } from '../components/admin/GalleryTab';
import { EditPricingDialog } from '../components/admin/EditPricingDialog';

export const AdminDashboard = () => {
  const [adminKey, setAdminKey] = useState(localStorage.getItem('admin_key') || '');
  const [authenticated, setAuthenticated] = useState(false);
  const [quotes, setQuotes] = useState([]);
  const [archivedQuotes, setArchivedQuotes] = useState([]);
  const [handymen, setHandymen] = useState([]);
  const [loading, setLoading] = useState(false);
  const [expandedQuote, setExpandedQuote] = useState(null);
  const [expandedHandyman, setExpandedHandyman] = useState(null);
  const [editingQuote, setEditingQuote] = useState(null);
  const [editPricing, setEditPricing] = useState({ price_min: '', price_max: '', total_cost: '', admin_notes: '' });
  const [actionLoading, setActionLoading] = useState(null);
  const [activeTab, setActiveTab] = useState('pending');
  const [filterStatus, setFilterStatus] = useState('pending_review');
  const [handymanFilter, setHandymanFilter] = useState('pending');
  const [galleryItems, setGalleryItems] = useState([]);
  const [selectedQuotes, setSelectedQuotes] = useState(new Set());
  const [bulkLoading, setBulkLoading] = useState(false);
  const [searchQuery, setSearchQuery] = useState('');

  const fetchData = useCallback(async () => {
    setLoading(true);
    try {
      const [quotesRes, archivedRes, handymenRes, galleryRes] = await Promise.all([
        axios.get(`${API}/admin/quotes?admin_key=${adminKey}&status=${filterStatus === 'all' ? '' : filterStatus}`),
        axios.get(`${API}/admin/quotes?admin_key=${adminKey}&status=archived`),
        axios.get(`${API}/admin/handymen?admin_key=${adminKey}`),
        axios.get(`${API}/admin/gallery?admin_key=${adminKey}`).catch(() => ({ data: [] }))
      ]);
      setQuotes(quotesRes.data);
      setArchivedQuotes(archivedRes.data);
      setHandymen(handymenRes.data);
      setGalleryItems(galleryRes.data || []);
    } catch (error) {
      console.error('Failed to fetch data:', error);
      if (error.response?.status === 403) {
        setAuthenticated(false);
        localStorage.removeItem('admin_key');
      }
    } finally {
      setLoading(false);
    }
  }, [adminKey, filterStatus]);

  useEffect(() => {
    if (authenticated) {
      fetchData();
    }
  }, [authenticated, filterStatus, fetchData]);

  // --- Auth ---
  const handleLogin = async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
      await axios.get(`${API}/admin/quotes/pending?admin_key=${adminKey}`);
      localStorage.setItem('admin_key', adminKey);
      setAuthenticated(true);
      toast.success('Admin access granted');
    } catch (error) {
      toast.error('Invalid admin key');
    } finally {
      setLoading(false);
    }
  };

  // --- Quote Actions ---
  const handleApproveQuote = async (quoteId, withEdits = false) => {
    setActionLoading(quoteId);
    try {
      const payload = withEdits ? {
        price_min: parseFloat(editPricing.price_min) || undefined,
        price_max: parseFloat(editPricing.price_max) || undefined,
        total_cost: parseFloat(editPricing.total_cost) || undefined,
        admin_notes: editPricing.admin_notes || undefined
      } : {};
      await axios.post(`${API}/admin/quotes/${quoteId}/approve?admin_key=${adminKey}`, payload);
      toast.success('Quote approved');
      setEditingQuote(null);
      fetchData();
    } catch (error) {
      toast.error('Failed to approve quote');
    } finally {
      setActionLoading(null);
    }
  };

  const handleRejectQuote = async (quoteId, reason) => {
    setActionLoading(quoteId);
    try {
      await axios.post(`${API}/admin/quotes/${quoteId}/reject?admin_key=${adminKey}&reason=${encodeURIComponent(reason || '')}`);
      toast.success('Quote rejected');
      fetchData();
    } catch (error) {
      toast.error('Failed to reject quote');
    } finally {
      setActionLoading(null);
    }
  };

  const handleRequestInspection = async (quoteId) => {
    setActionLoading(quoteId);
    try {
      await axios.post(`${API}/admin/quotes/${quoteId}/request-inspection?admin_key=${adminKey}`);
      toast.success('Inspection requested');
      fetchData();
    } catch (error) {
      toast.error('Failed to request inspection');
    } finally {
      setActionLoading(null);
    }
  };

  const handleArchiveQuote = async (quoteId) => {
    if (!window.confirm('Archive this quote? It will be hidden from the main view but can be restored later.')) return;
    setActionLoading(quoteId);
    try {
      await axios.post(`${API}/admin/quotes/${quoteId}/archive?admin_key=${adminKey}`);
      toast.success('Quote archived');
      fetchData();
    } catch (error) {
      toast.error('Failed to archive quote');
    } finally {
      setActionLoading(null);
    }
  };

  const handleDeleteQuote = async (quoteId) => {
    if (!window.confirm('Delete this quote permanently? This cannot be undone.')) return;
    setActionLoading(quoteId);
    try {
      await axios.delete(`${API}/admin/quotes/${quoteId}?admin_key=${adminKey}`);
      toast.success('Quote deleted permanently');
      fetchData();
    } catch (error) {
      toast.error('Failed to delete quote');
    } finally {
      setActionLoading(null);
    }
  };

  const handleRestoreQuote = async (quoteId) => {
    setActionLoading(quoteId);
    try {
      await axios.post(`${API}/admin/quotes/${quoteId}/restore?admin_key=${adminKey}`);
      toast.success('Quote restored');
      fetchData();
    } catch (error) {
      toast.error('Failed to restore quote');
    } finally {
      setActionLoading(null);
    }
  };

  // --- Handyman Actions ---
  const handleApproveHandyman = async (handymanId) => {
    setActionLoading(handymanId);
    try {
      await axios.post(`${API}/admin/handymen/${handymanId}/approve?admin_key=${adminKey}`);
      toast.success('Handyman approved');
      fetchData();
    } catch (error) {
      toast.error('Failed to approve handyman');
    } finally {
      setActionLoading(null);
    }
  };

  const handleRejectHandyman = async (handymanId) => {
    setActionLoading(handymanId);
    try {
      await axios.post(`${API}/admin/handymen/${handymanId}/reject?admin_key=${adminKey}`);
      toast.success('Handyman rejected');
      fetchData();
    } catch (error) {
      toast.error('Failed to reject handyman');
    } finally {
      setActionLoading(null);
    }
  };

  const handleArchiveHandyman = async (handymanId) => {
    if (!window.confirm('Archive this application?')) return;
    setActionLoading(handymanId);
    try {
      await axios.post(`${API}/admin/handymen/${handymanId}/archive?admin_key=${adminKey}`);
      toast.success('Handyman archived');
      fetchData();
    } catch (error) {
      toast.error('Failed to archive handyman');
    } finally {
      setActionLoading(null);
    }
  };

  const handleDeleteHandyman = async (handymanId) => {
    if (!window.confirm('Delete this application permanently? This cannot be undone.')) return;
    setActionLoading(handymanId);
    try {
      await axios.delete(`${API}/admin/handymen/${handymanId}?admin_key=${adminKey}`);
      toast.success('Handyman deleted permanently');
      fetchData();
    } catch (error) {
      toast.error('Failed to delete handyman');
    } finally {
      setActionLoading(null);
    }
  };

  // --- Selection & Bulk Actions ---
  const toggleSelect = (quoteId) => {
    setSelectedQuotes(prev => {
      const next = new Set(prev);
      if (next.has(quoteId)) next.delete(quoteId);
      else next.add(quoteId);
      return next;
    });
  };

  const selectAllInList = (list) => {
    const allIds = list.map(q => q.id);
    const allSelected = allIds.every(id => selectedQuotes.has(id));
    if (allSelected) {
      setSelectedQuotes(prev => {
        const next = new Set(prev);
        allIds.forEach(id => next.delete(id));
        return next;
      });
    } else {
      setSelectedQuotes(prev => {
        const next = new Set(prev);
        allIds.forEach(id => next.add(id));
        return next;
      });
    }
  };

  const handleBulkAction = async (action) => {
    if (selectedQuotes.size === 0) return;
    const label = action === 'delete' ? 'delete' : action === 'archive' ? 'archive' : action === 'highlight' ? 'highlight' : 'unhighlight';
    if (action === 'delete' && !window.confirm(`Permanently delete ${selectedQuotes.size} quote(s)?`)) return;
    setBulkLoading(true);
    try {
      await axios.post(`${API}/admin/quotes/bulk?admin_key=${adminKey}`, {
        quote_ids: Array.from(selectedQuotes),
        action
      });
      toast.success(`${selectedQuotes.size} quote(s) ${label}${label.endsWith('e') ? 'd' : 'ed'}`);
      setSelectedQuotes(new Set());
      fetchData();
    } catch (error) {
      toast.error(`Failed to ${label} quotes`);
    } finally {
      setBulkLoading(false);
    }
  };

  const openEditDialog = (quote) => {
    setEditingQuote(quote);
    setEditPricing({
      price_min: quote.price_min || '',
      price_max: quote.price_max || '',
      total_cost: quote.total_cost || '',
      admin_notes: ''
    });
  };

  // --- Pre-auth screen ---
  if (!authenticated) {
    return <AdminLogin adminKey={adminKey} setAdminKey={setAdminKey} onLogin={handleLogin} loading={loading} />;
  }

  const pendingQuotes = quotes.filter(q => q.status === 'pending_review');
  const approvedQuotes = quotes.filter(q => q.status === 'approved');
  const pendingHandymen = handymen.filter(h => h.status === 'pending');
  const totalQuoteValue = quotes.reduce((sum, q) => sum + (q.price_max || q.total_cost || 0), 0);
  const avgQuoteValue = quotes.length > 0 ? `£${(totalQuoteValue / quotes.length).toFixed(0)}` : '£0';

  // Search filter
  const sq = searchQuery.toLowerCase().trim();
  const matchesQuote = (q) => !sq || [q.user_name, q.user_email, q.user_phone, q.address, q.service_name].some(f => (f || '').toLowerCase().includes(sq));
  const matchesHandyman = (h) => !sq || [h.name, h.email, h.phone, h.coverage_areas].some(f => (f || '').toLowerCase().includes(sq));

  const filteredPending = pendingQuotes.filter(matchesQuote);
  const filteredQuotes = quotes.filter(matchesQuote);
  const filteredArchived = archivedQuotes.filter(matchesQuote);
  const filteredHandymen = handymen.filter(matchesHandyman);
  const showSearch = ['pending', 'all', 'archived', 'handymen'].includes(activeTab);

  // Shared props for quote lists
  const quoteListHandlers = {
    onToggleExpand: (id) => setExpandedQuote(expandedQuote === id ? null : id),
    onApproveQuote: handleApproveQuote,
    onRejectQuote: handleRejectQuote,
    onRequestInspection: handleRequestInspection,
    onEditQuote: openEditDialog,
    onArchiveQuote: handleArchiveQuote,
    onDeleteQuote: handleDeleteQuote,
    onRestoreQuote: handleRestoreQuote,
    actionLoading,
    selectedQuotes,
    onSelectQuote: toggleSelect,
    onSelectAll: selectAllInList,
    onBulkAction: handleBulkAction,
    bulkLoading,
  };

  return (
    <div className="min-h-[calc(100vh-4rem)] bg-muted/30 py-4 md:py-8" data-testid="admin-dashboard">
      <div className="max-w-7xl mx-auto px-3 sm:px-6 lg:px-8">
        {/* Header */}
        <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-3 mb-6">
          <div>
            <h1 className="text-xl md:text-3xl font-bold font-['Outfit']">Admin Dashboard</h1>
            <p className="text-sm text-muted-foreground">Review quotes and manage handymen</p>
          </div>
          <div className="flex items-center gap-2">
            <Badge variant="outline" className="text-yellow-600 border-yellow-300 text-xs md:text-sm">
              {pendingQuotes.length} Pending
            </Badge>
            <Button
              variant="outline"
              size="sm"
              onClick={() => { setAuthenticated(false); localStorage.removeItem('admin_key'); }}
            >
              Logout
            </Button>
          </div>
        </div>

        <StatsCards
          pendingCount={pendingQuotes.length}
          approvedCount={approvedQuotes.length}
          avgQuoteValue={avgQuoteValue}
          pendingHandymenCount={pendingHandymen.length}
        />

        <Tabs value={activeTab} onValueChange={setActiveTab}>
          <TabsList className="mb-4 md:mb-6 w-full justify-start overflow-x-auto">
            <TabsTrigger value="pending" data-testid="tab-pending" className="text-xs md:text-sm whitespace-nowrap">
              Pending ({pendingQuotes.length})
            </TabsTrigger>
            <TabsTrigger value="all" data-testid="tab-all" className="text-xs md:text-sm whitespace-nowrap">
              All Quotes
            </TabsTrigger>
            <TabsTrigger value="archived" data-testid="tab-archived" className="text-xs md:text-sm whitespace-nowrap">
              Archived ({archivedQuotes.length})
            </TabsTrigger>
            <TabsTrigger value="handymen" data-testid="tab-handymen" className="text-xs md:text-sm whitespace-nowrap">
              Handymen ({handymen.length})
            </TabsTrigger>
            <TabsTrigger value="gallery" data-testid="tab-gallery" className="text-xs md:text-sm whitespace-nowrap">
              Gallery ({galleryItems.length})
            </TabsTrigger>
          </TabsList>

          {/* Search Bar */}
          {showSearch && (
            <div className="relative mb-4">
              <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-muted-foreground" />
              <Input
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
                placeholder="Search by name, email, phone or address..."
                className="pl-9 pr-9 text-sm"
                data-testid="admin-search-input"
              />
              {searchQuery && (
                <button
                  onClick={() => setSearchQuery('')}
                  className="absolute right-3 top-1/2 -translate-y-1/2 text-muted-foreground hover:text-foreground"
                  data-testid="admin-search-clear"
                >
                  <X className="w-4 h-4" />
                </button>
              )}
            </div>
          )}

          {/* Pending Quotes */}
          <TabsContent value="pending">
            <QuotesList
              quotes={filteredPending}
              expandedQuote={expandedQuote}
              emptyIcon={Check}
              emptyTitle={sq ? "No matching quotes" : "All caught up!"}
              emptyDescription={sq ? `No pending quotes match "${searchQuery}"` : "No quotes pending review"}
              bulkActions={['highlight', 'archive', 'delete']}
              {...quoteListHandlers}
            />
          </TabsContent>

          {/* All Quotes */}
          <TabsContent value="all">
            <div className="mb-4 flex flex-wrap gap-2">
              {['all', 'pending_review', 'approved', 'rejected', 'inspection_required', 'booked', 'archived'].map((status) => (
                <Button
                  key={status}
                  variant={filterStatus === status ? 'default' : 'outline'}
                  size="sm"
                  onClick={() => setFilterStatus(status)}
                  className="text-xs md:text-sm"
                >
                  {status === 'all' ? 'All' : status === 'pending_review' ? 'Pending' : status === 'inspection_required' ? 'Inspect' : status.replace('_', ' ')}
                </Button>
              ))}
            </div>
            <QuotesList
              quotes={filteredQuotes}
              expandedQuote={expandedQuote}
              loading={loading}
              emptyIcon={FileText}
              emptyTitle={sq ? "No matching quotes" : "No quotes found"}
              showStatus
              bulkActions={['highlight', 'unhighlight', 'archive', 'delete']}
              {...quoteListHandlers}
            />
          </TabsContent>

          {/* Archived */}
          <TabsContent value="archived">
            <QuotesList
              quotes={filteredArchived}
              expandedQuote={expandedQuote}
              emptyIcon={Archive}
              emptyTitle={sq ? "No matching quotes" : "No Archived Quotes"}
              emptyDescription={sq ? `No archived quotes match "${searchQuery}"` : "Archived quotes will appear here. You can archive quotes from the Pending or All Quotes tabs."}
              showStatus
              bulkActions={['highlight', 'delete']}
              {...quoteListHandlers}
            />
          </TabsContent>

          {/* Handymen */}
          <TabsContent value="handymen">
            <HandymenTab
              handymen={filteredHandymen}
              handymanFilter={handymanFilter}
              setHandymanFilter={setHandymanFilter}
              expandedHandyman={expandedHandyman}
              setExpandedHandyman={setExpandedHandyman}
              onApprove={handleApproveHandyman}
              onReject={handleRejectHandyman}
              onArchive={handleArchiveHandyman}
              onDelete={handleDeleteHandyman}
              actionLoading={actionLoading}
              searchQuery={searchQuery}
            />
          </TabsContent>

          {/* Gallery */}
          <TabsContent value="gallery">
            <GalleryTab
              galleryItems={galleryItems}
              adminKey={adminKey}
              onRefresh={fetchData}
            />
          </TabsContent>
        </Tabs>
      </div>

      <EditPricingDialog
        editingQuote={editingQuote}
        setEditingQuote={setEditingQuote}
        editPricing={editPricing}
        setEditPricing={setEditPricing}
        onSaveApprove={(id) => handleApproveQuote(id, true)}
        loading={actionLoading}
      />
    </div>
  );
};

export default AdminDashboard;
